import { Check, ArrowUpRight, ShieldCheck, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { track, attributedCheckoutUrl } from "@/lib/track";

const STRIPE_PAYMENT_LINK = process.env.REACT_APP_STRIPE_PAYMENT_LINK;

const INCLUDES = [
  "10K30 Longevity Foundation — mandatory first 12 weeks",
  "Goal-based pathways — LEAN, BUILD, STRONG, MAINTAIN",
  "Gym + Home training tracks",
  "Progressive walking system",
  "Eat Library — whole-food meals",
  "Weekly + phase-based education",
  "Rebuild Score tracking",
  "Member portal + community",
];

const JOURNEY = [
  ["JOIN", "One membership. $59/month."],
  ["RESET", "Complete the 12-week 10K30 Longevity Foundation."],
  ["REBUILD", "Choose LEAN / BUILD / STRONG / MAINTAIN."],
  ["STAY BUILT", "Keep going with ongoing pathways + education."],
];

export default function Join() {
  useEffect(() => { track("view_join_page"); }, []);
  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-16">
      <div className="label-eyebrow">BA30 Membership</div>
      <h1 className="wordmark mt-4 text-5xl md:text-7xl uppercase leading-[0.9]">
        Join <span className="text-primary">BA30.</span>
      </h1>
      <p className="mt-4 max-w-2xl text-foreground/75" data-testid="join-pricing-line">
        $59/month. Starts today with the 12-week 10K30 Longevity Foundation. Cancel anytime.
      </p>

      <div className="mt-10 grid lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-7 bg-foreground text-background rounded-lg p-8 md:p-10">
          <div className="label-eyebrow !text-primary">BA30 Membership</div>
          <div className="mt-6 flex items-baseline gap-2">
            <div className="wordmark text-6xl md:text-7xl leading-none text-primary" data-testid="join-price">$59</div>
            <div className="wordmark text-2xl md:text-3xl text-background/70">/ month</div>
          </div>
          <p className="mt-4 text-sm text-background/75 max-w-md">
            Start with the 12-week 10K30 Longevity Foundation. Build your foundation. Then move into the pathway that matches your goal. Your BA30 membership continues at $59/month.
          </p>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="join-journey">
            {JOURNEY.map(([k, v], i) => (
              <div key={k} className="rounded-md border border-background/15 p-3">
                <div className="text-[10px] tracking-widest text-background/40">0{i + 1}</div>
                <div className="font-head uppercase text-sm mt-1">{k}</div>
                <div className="text-[11px] text-background/65 mt-1 leading-snug">{v}</div>
              </div>
            ))}
          </div>

          <ul className="mt-8 grid md:grid-cols-2 gap-2 text-sm text-background/85">
            {INCLUDES.map((i) => (
              <li key={i} className="flex gap-2"><Check className="text-primary shrink-0 mt-0.5" size={14} strokeWidth={2.5} />{i}</li>
            ))}
          </ul>
          <p className="mt-6 text-xs text-background/60">
            Recurring AUD $59/month, charged today and then monthly until cancelled. No new checkout when you move from 10K30 into your next pathway. Guided self-service — not 1:1 coaching.
          </p>
        </div>

        <div className="lg:col-span-5 bg-card border border-border rounded-lg p-8 space-y-5" data-testid="join-cta-card">
          <div className="label-eyebrow">Start Building for Longer</div>
          <p className="text-sm text-foreground/80 leading-relaxed">
            You'll complete secure checkout on Stripe. After payment, we'll email you a link to set your password and unlock your member portal — Week 1 of 10K30 is waiting.
          </p>

          {STRIPE_PAYMENT_LINK ? (
            <a href={attributedCheckoutUrl(STRIPE_PAYMENT_LINK)} target="_blank" rel="noopener noreferrer"
               className="btn-primary w-full flex items-center justify-center gap-2 !text-base !py-4"
               data-testid="join-payment-link" data-event="begin_checkout">
              Start Building for Longer — $59/month <ArrowUpRight size={18} strokeWidth={2.5} />
            </a>
          ) : (
            <div className="rounded-md bg-destructive/10 text-destructive p-3 text-sm">
              Payment link not configured. Please contact <a href="mailto:support@builtafter30.com" className="underline">support@builtafter30.com</a>.
            </div>
          )}

          <div className="pt-4 border-t border-border space-y-3 text-xs text-muted-foreground leading-relaxed">
            <div className="flex items-start gap-2">
              <ShieldCheck size={14} className="shrink-0 mt-0.5 text-foreground/70" />
              <span>Payments are processed by Stripe. BA30 never sees or stores your card details.</span>
            </div>
            <div className="flex items-start gap-2">
              <Mail size={14} className="shrink-0 mt-0.5 text-foreground/70" />
              <span>
                Your welcome email lands in the inbox you used at checkout. Not there in 5 minutes? Check spam or email{" "}
                <a href="mailto:support@builtafter30.com" className="underline text-foreground/80 hover:text-foreground">support@builtafter30.com</a>.
              </span>
            </div>
          </div>

          <div className="pt-4 border-t border-border text-xs text-muted-foreground">
            Already a member?{" "}
            <Link to="/login" className="underline text-foreground/80 hover:text-foreground" data-testid="join-login-link">Sign in →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
